/* ---------------------------------------------------------------------
   Editing what VRO remembers.

   The cards in sections/profile-memories.liquid are theme blocks — the
   merchant's copy, the same for everyone. A shopper reading "Your skin
   runs oily" and finding it wrong had no way to say so: the ⋯ button was
   inert markup.

   It now opens a menu with Edit, and an edit is kept in the shopper's own
   personalization profile (assets/vro-personalization.js), filed against
   the block's id. That is deliberate on two counts: a storefront cannot
   write back to a theme block, and it should not — one shopper correcting
   their own memory must not change what every other shopper reads. The
   block stays the default and the override sits on top of it, which also
   means "Undo my edit" is just dropping the override.

   Everything degrades quietly. No profile store, or storage the browser
   refuses, and the cards read exactly as the theme wrote them.
--------------------------------------------------------------------- */
(function () {
  'use strict';

  /* This file is included by the Profile memories section, which renders
     twice on the profile page (the page itself and the profile pop-up in
     layout/theme.liquid). Run once: every handler here is delegated on the
     document and covers both lists, and running twice toggled each ⋯ menu
     open and straight back shut. */
  if (window.__vroProfileMemories) return;
  window.__vroProfileMemories = true;

  function api() {
    return (window.VroPersonalization && window.VroPersonalization.profile) || null;
  }

  /* Which memories a facet is worth, asked of the store so the cap and the
     cards agree. The store is loaded site-wide and this file only on the
     profile page, so it is always there in practice; the fallback keeps a
     missing script from emptying the page rather than costing it one
     section. */
  function memoryIds(name, value) {
    var p = api();
    if (p && p.memoryIdsFor) {
      try { return p.memoryIdsFor(name, value) || []; } catch (e) { /* fall through */ }
    }
    var base = String(name).split('@')[0];
    if (base === 'size_memory') {
      if (!value || typeof value !== 'object') return [];
      var ids = [];
      if (value.alpha && value.alpha.value) ids.push('vro-size-alpha');
      if (value.numeric && value.numeric.value) ids.push('vro-size-numeric');
      return ids;
    }
    if (base === 'answer') {
      if (!value || typeof value !== 'object' || !value.a) return [];
      return ['vro-answer-' + lower(String(name).split('@')[1] || name).replace(/\W+/g, '-')];
    }
    if (typeof value !== 'string' || !value.trim()) return [];
    if (base === 'body') return ['vro-body'];
    if (base === 'skin' || base === 'hair' || base === 'goal' || base === 'size') {
      return ['vro-' + base + '-' + lower(value).replace(/\W+/g, '-')];
    }
    return [];
  }

  /* The whole override map, or an empty one. Never null, so callers can
     read it without guarding every time. */
  function all() {
    var p = api();
    if (!p) return {};
    try {
      var profile = p.get();
      return (profile && profile.memories) || {};
    } catch (e) {
      return {};
    }
  }

  function write(id, text) {
    var p = api();
    if (!p) return false;
    try {
      var profile = p.get();
      profile.memories = profile.memories || {};
      if (text === null) delete profile.memories[id];
      else profile.memories[id] = text;
      p.save(profile);
      return true;
    } catch (e) {
      return false;
    }
  }

  function cardParts(card) {
    return {
      id: card.getAttribute('data-memory-id'),
      text: card.querySelector('[data-memory-text]'),
      fallback: card.querySelector('[data-memory-default]'),
      menu: card.querySelector('[data-memory-menu]'),
      more: card.querySelector('[data-memory-more]'),
      reset: card.querySelector('[data-memory-reset]')
    };
  }

  /* What the merchant wrote, read from the template rather than from the
     paragraph — by the time anyone asks, the paragraph may be holding the
     shopper's version. */
  function original(parts) {
    if (!parts.fallback) return '';
    return (parts.fallback.content ? parts.fallback.content.textContent : parts.fallback.textContent) || '';
  }

  function closeMenu(card) {
    var parts = cardParts(card);
    if (parts.menu) parts.menu.hidden = true;
    if (parts.more) parts.more.setAttribute('aria-expanded', 'false');
  }

  function closeAllMenus(except) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-memory-card]'), function (card) {
      if (card !== except) closeMenu(card);
    });
  }

  /* Reflects one card against the stored overrides: the wording it shows,
     whether it is marked as edited, and whether undo is on offer. */
  function paint(card, overrides) {
    var parts = cardParts(card);
    if (!parts.text) return;

    var mine = parts.id ? overrides[parts.id] : null;
    var edited = typeof mine === 'string' && mine.length > 0;

    parts.text.textContent = edited ? mine : original(parts).trim();
    card.classList.toggle('is-edited', edited);
    if (parts.reset) parts.reset.hidden = !edited;
  }

  /* The editor is gone with the Edit item. A memory is what the shopper
     answered, not a field they keep — the ⋯ menu removes one and the quiz
     records the next. openEditor/save/closeEditor and their markup went
     with it rather than staying unreachable behind a button that is no
     longer drawn. */

  var MENU_DEFAULT =
    '<button type="button" class="profile-memory-menu-item profile-memory-menu-item--danger" data-memory-remove>' +
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M3 6h18"/><path d="M8 6V4h8v2"/><path d="M19 6l-1 14H6L5 6"/></svg>Remove</button>';

  var MENU_CONFIRM =
    '<p class="profile-memory-menu-ask">Remove this memory? You will need to take the quiz again to get it back.</p>' +
    '<div class="profile-memory-menu-actions">' +
      '<button type="button" class="profile-memory-menu-item profile-memory-menu-item--danger" data-memory-remove-confirm>Remove</button>' +
      '<button type="button" class="profile-memory-menu-item" data-memory-remove-cancel>Keep</button>' +
    '</div>';

  function resetMenu(card) {
    var parts = cardParts(card);
    if (parts.menu) parts.menu.innerHTML = MENU_DEFAULT;
  }

  function askRemove(card) {
    var parts = cardParts(card);
    if (!parts.menu) return;
    parts.menu.innerHTML = MENU_CONFIRM;
    var confirmBtn = parts.menu.querySelector('[data-memory-remove-confirm]');
    if (confirmBtn) confirmBtn.focus();
  }

  /* Removing a memory deletes the answer it was written from, not the
     sentence. Deleting only the sentence would leave the quiz answered and
     the card would come straight back on the next render; deleting the
     answer is what makes the quiz ask again, which is the way back the
     confirm promises. */
  function removeMemory(card) {
    var parts = cardParts(card);
    var facet = card.getAttribute('data-memory-facet');
    var p = api();
    if (p) {
      try {
        var profile = p.get() || {};
        if (parts.id === 'vro-quiz') delete profile.quiz;
        if (facet && profile.facets) delete profile.facets[facet];
        /* Any hand-edited text filed under this id goes too, or it would
           survive to decorate whatever answer is recorded next. */
        if (profile.memories) delete profile.memories[parts.id];
        p.save(profile);
      } catch (err) { /* storage blocked; the card still goes below */ }
    }
    closeMenu(card);
    card.classList.add('profile-memory-card--going');
    /* Matches the fold in custom.css. Removed on transitionend as well, so
       a browser that never fires it still loses the card. */
    var drop = function () { if (card.parentNode) card.parentNode.removeChild(card); };
    card.addEventListener('transitionend', drop, { once: true });
    setTimeout(drop, 400);
  }

  function onClick(e) {
    var card = e.target.closest ? e.target.closest('[data-memory-card]') : null;

    if (!card) { closeAllMenus(null); return; }

    if (e.target.closest('[data-memory-more]')) {
      var parts = cardParts(card);
      if (!parts.menu) return;
      var open = parts.menu.hidden;
      closeAllMenus(card);
      parts.menu.hidden = !open;
      parts.more.setAttribute('aria-expanded', open ? 'true' : 'false');
      return;
    }

    /* Remove asks first. It is the one irreversible thing on this page —
       the memory goes and the only way back is answering that quiz again —
       and a menu item sitting under a ⋯ is easy to hit by accident. The
       confirm replaces the menu's contents rather than opening a dialog,
       so the question appears where the finger already is. */
    if (e.target.closest('[data-memory-remove]')) { askRemove(card); return; }
    if (e.target.closest('[data-memory-remove-cancel]')) { closeMenu(card); resetMenu(card); return; }
    if (e.target.closest('[data-memory-remove-confirm]')) { removeMemory(card); return; }

    /* A click anywhere else on a card still dismisses an open menu. */
    closeAllMenus(null);
  }

  function onKey(e) {
    var card = e.target.closest ? e.target.closest('[data-memory-card]') : null;
    if (!card) {
      if (e.key === 'Escape') closeAllMenus(null);
      return;
    }
    /* Escape backs out of whatever the ⋯ opened — the confirm if it is
       showing, otherwise the menu itself. Leaving a half-asked "Remove
       this?" on screen after Escape would be the one state here that
       cannot be dismissed with the keyboard. */
    if (e.key === 'Escape') {
      resetMenu(card);
      closeAllMenus(null);
    }
  }

  /* ---------------------------------------------------------------
     Memories the shopper actually made.

     The quiz on the home page and the questions asked inside sections
     (skin, goal, body type) all save into the same personalization
     profile, and so do the sizes a shopper picks. Each becomes a card here,
     in the blocks' own markup, ahead of the blocks. Ids are stable per
     memory ("vro-quiz", "vro-skin", ...) so an edit made to one survives a
     reload exactly as an edit to a block does.
  --------------------------------------------------------------- */
  var SPARKLE =
    '<svg width="16" height="16" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12.2825 8.32959C13.1923 6.6654 13.6473 5.83331 13.9951 5.83331C14.343 5.83331 14.7979 6.6654 15.7078 8.32959C16.6442 10.0425 17.8957 11.3087 19.657 12.2733C21.3241 13.1863 22.1576 13.6427 22.1576 13.991C22.1576 14.3392 21.3235 14.7962 19.6554 15.7101C17.9443 16.6475 16.6794 17.9004 15.7157 19.6636C14.8037 21.3322 14.3477 22.1666 13.9998 22.1666C13.652 22.1666 13.1955 21.3318 12.2825 19.6618C11.3461 17.9489 10.0945 16.6827 8.33317 15.7181C6.66053 14.8021 5.82422 14.344 5.82422 13.9958C5.82422 13.6474 6.66053 13.1894 8.33317 12.2733C10.0945 11.3087 11.3461 10.0425 12.2825 8.32959Z" fill="#0080FF" stroke="#0080FF" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  var SKIN_LINES = {
    'gets oily by noon': 'Your skin runs oily — it gets shiny by noon.',
    'feels tight and dry': 'Your skin runs dry and feels tight.',
    'a bit of both': 'Your skin is combination — a bit oily, a bit dry.',
    'reacts to most things': 'Your skin is sensitive and reacts to most things.'
  };

  function lower(v) { return String(v || '').trim().toLowerCase(); }
  function cap(v) { v = String(v || '').trim(); return v.charAt(0).toUpperCase() + v.slice(1); }

  function profileNow() {
    var p = api();
    if (!p) return null;
    try { return p.get() || null; } catch (e) { return null; }
  }

  /* The facet keys carry the section they were answered in
     ("skin@shopify-section-…"); a memory is about the shopper, not the
     section, so the same answer given in two places is one memory. */
  function facetLines(facets) {
    var out = [];
    var seen = {};
    /* Which facet the line being built came from. Set once per key below
       rather than threaded through every add() call — there are a dozen of
       them and they would all be passing the same value. Removing a memory
       deletes this facet, which is also what re-arms its quiz. */
    var currentFacet = null;
    function add(id, tags, text, source) {
      var key = id + '|' + lower(text);
      if (seen[key]) return;
      seen[key] = true;
      out.push({ id: id, tags: tags, text: text, source: source, facet: currentFacet });
    }
    Object.keys(facets || {}).forEach(function (key) {
      currentFacet = key;
      var value = facets[key];
      var base = key.split('@')[0];
      /* The id comes from the store rather than from here. It is what the
         fifteen-memory cap counts, and a card that rendered under one id
         while the cap counted another would be pruned by a rule that does
         not match what the shopper is looking at. One rule, one copy: see
         memoryIdsFor in assets/vro-personalization.js. An empty list means
         this key says nothing a shopper would recognise as a memory, which
         is also the answer to a value of the wrong shape. */
      var ids = memoryIds(key, value);
      if (!ids.length) return;

      if (base === 'answer') {
        var q = String(value.q || '').trim().replace(/[?:]+$/, '');
        add(ids[0], [value.tag || 'Quiz'],
          'You said \u201c' + String(value.a).trim() + '\u201d when we asked \u201c' + q + '?\u201d',
          'From Quiz Card');
        return;
      }
      if (base === 'size_memory') {
        if (ids.indexOf('vro-size-alpha') !== -1) {
          add('vro-size-alpha', ['Fit', 'Fashion'],
            'You usually wear ' + (/^(XS|S|M|L|XL|XXL|XXXL)$/i.test(String(value.alpha.value)) ? 'an ' : 'a ') + value.alpha.value + (value.alpha.brand ? ' (last picked in ' + value.alpha.brand + ')' : '') + '.',
            'From the sizes you picked');
        }
        if (ids.indexOf('vro-size-numeric') !== -1) {
          add('vro-size-numeric', ['Fit', 'Fashion'],
            'Your waist size is ' + value.numeric.value + '.',
            'From the sizes you picked');
        }
        return;
      }
      if (base === 'skin') {
        add(ids[0], ['Grooming'],
          SKIN_LINES[lower(value)] || ('Your skin: ' + lower(value) + '.'), 'From Quiz Card');
      } else if (base === 'hair') {
        add(ids[0], ['Grooming'],
          'Your hair: ' + lower(value) + '.', 'From Quiz Card');
      } else if (base === 'goal') {
        add(ids[0], ['Health'],
          "You're working towards " + lower(value) + '.', 'From Quiz Card');
      } else if (base === 'body') {
        add(ids[0], ['Fit', 'Fashion'],
          'Your body type is ' + cap(value) + ', so we lead with cuts that suit it.', 'From Quiz Card');
      } else if (base === 'size') {
        add(ids[0], ['Fit', 'Fashion'],
          'You usually wear size ' + String(value).trim() + '.', 'From the size quiz');
      }
    });
    return out;
  }

  function quizLine(quiz) {
    if (!quiz || !quiz.category) return null;
    var problems = (quiz.problems || []).filter(Boolean).map(lower);
    var text = problems.length
      ? 'You told us about ' + problems.join(' and ')
      : 'You were shopping for ' + lower(quiz.category);
    if (quiz.condition) text += ' (' + lower(quiz.condition) + ')';
    if (quiz.budget) text += ', and you keep it within ' + quiz.budget;
    text += '.';
    var tags = [String(quiz.category).trim()];
    if (quiz.budget) tags.push('Budget');
    return { id: 'vro-quiz', tags: tags, text: text, source: 'From Quiz Card' };
  }

  /* Newest first: the answer given a minute ago leads the list, the
     oldest sits at the foot. Each answer carries the time it was saved
     (profile.facetAt per facet, profile.quizAt for the quiz); anything
     saved before those were kept counts as oldest and keeps its order. */
  function memoriesFrom(profile) {
    if (!profile) return [];
    var list = [];
    var q = quizLine(profile.quiz);
    if (q) { q.at = profile.quizAt || 0; list.push(q); }
    var at = profile.facetAt || {};
    facetLines(profile.facets).forEach(function (m) {
      m.at = (m.facet && at[m.facet]) || 0;
      list.push(m);
    });
    return list
      .map(function (m, i) { return { m: m, i: i }; })
      .sort(function (a, b) { return (b.m.at - a.m.at) || (a.i - b.i); })
      .map(function (x) { return x.m; });
  }

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined && text !== null) node.textContent = text;
    return node;
  }

  function cardFor(memory) {
    var card = el('div', 'profile-memory-card');
    card.setAttribute('data-memory-card', '');
    card.setAttribute('data-memory-live', '');
    card.setAttribute('data-memory-id', memory.id);
    if (memory.facet) card.setAttribute('data-memory-facet', memory.facet);

    var head = el('div', 'd-flex justify-content-between align-items-start mb-2');
    var tags = el('div', 'profile-memory-tags');
    memory.tags.slice(0, 2).forEach(function (t) { if (t) tags.appendChild(el('span', 'profile-memory-tag', t)); });
    head.appendChild(tags);

    var wrap = el('div', 'profile-memory-menu-wrap');
    var more = el('button', 'profile-memory-more', '⋯');
    more.type = 'button';
    more.setAttribute('data-memory-more', '');
    more.setAttribute('aria-label', 'Memory options');
    more.setAttribute('aria-haspopup', 'true');
    more.setAttribute('aria-expanded', 'false');
    var menu = el('div', 'profile-memory-menu');
    menu.setAttribute('data-memory-menu', '');
    menu.hidden = true;
    menu.innerHTML = MENU_DEFAULT;
    wrap.appendChild(more);
    wrap.appendChild(menu);
    head.appendChild(wrap);
    card.appendChild(head);

    var text = el('p', 'profile-memory-text mb-2', memory.text);
    text.setAttribute('data-memory-text', '');
    card.appendChild(text);

    var tpl = document.createElement('template');
    tpl.setAttribute('data-memory-default', '');
    if (tpl.content) tpl.content.appendChild(document.createTextNode(memory.text));
    else tpl.textContent = memory.text;
    card.appendChild(tpl);


    var source = el('p', 'profile-memory-source mb-0');
    var diamond = el('span', 'profile-memory-source-diamond');
    diamond.innerHTML = SPARKLE;
    source.appendChild(diamond);
    source.appendChild(document.createTextNode(memory.source));
    card.appendChild(source);
    return card;
  }

  function lists() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-memory-list]'));
  }

  function sectionOf(list) {
    return list.closest('.profile-section') || list.parentNode;
  }

  function visibleCount(list) {
    var n = parseInt(list.getAttribute('data-visible'), 10);
    return isFinite(n) && n > 0 ? n : 3;
  }

  /* Only the first few cards; the rest wait behind "See all N memories". */
  function applyLimit(list) {
    var root = sectionOf(list);
    var shown = Array.prototype.filter.call(
      list.querySelectorAll('[data-memory-card]'), function (c) { return !c.hidden; });
    var limit = visibleCount(list);
    shown.forEach(function (card, i) { card.classList.toggle('is-over-limit', i >= limit); });

    var total = shown.length;
    var link = root.querySelector('[data-memory-see-all]');
    if (link) {
      var label = link.querySelector('[data-memory-see-all-label]');
      var inPlace = (link.getAttribute('href') || '#') === '#';
      var expanded = list.classList.contains('is-expanded');
      if (label) {
        label.textContent = expanded && inPlace
          ? 'Show fewer memories'
          : 'See all ' + total + ' ' + (total === 1 ? 'memory' : 'memories');
      }
      link.hidden = inPlace ? total <= limit : total === 0;
      if (inPlace) link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    var sub = root.querySelector('[data-memory-subheading]');
    if (sub) {
      var template = sub.getAttribute('data-template') || sub.textContent;
      /* "15 memories shaping what you see" — the merchant's sentence, with
         the shopper's real number in it. */
      if (/\d+/.test(template)) {
        sub.textContent = template
          .replace(/\d+/, String(total))
          .replace(/\bmemories\b/, total === 1 ? 'memory' : 'memories');
      }
    }
  }

  function renderLive(list, profile) {
    Array.prototype.forEach.call(list.querySelectorAll('[data-memory-live]'), function (c) { c.remove(); });
    var memories = memoriesFrom(profile);
    var first = list.firstChild;
    memories.forEach(function (m) { list.insertBefore(cardFor(m), first); });

    /* The blocks are examples: by default they give way the moment the
       shopper has a memory of their own. */
    var mode = list.getAttribute('data-examples') || 'fallback';
    var inEditor = !!(window.Shopify && window.Shopify.designMode);
    var hideExamples = !inEditor && (mode === 'never' || (mode !== 'always' && memories.length > 0));
    Array.prototype.forEach.call(list.querySelectorAll('[data-memory-example]'), function (c) {
      c.hidden = hideExamples;
    });
    /* Nothing of the shopper's and no examples standing in: say so, rather
       than leave a heading over an empty list. */
    var empty = sectionOf(list).querySelector('[data-memory-empty]');
    if (empty) empty.hidden = !(memories.length === 0 && hideExamples);
    return memories.length;
  }

  var recsFor = null;
  function renderRecs(list, profile) {
    var root = sectionOf(list);
    var box = root.querySelector('[data-memory-recs]');
    if (!box) return;
    var engine = window.VroPersonalization;
    var quiz = profile && profile.quiz;
    if (!engine || !engine.renderInto || !quiz || !quiz.category) {
      box.hidden = true;
      recsFor = null;
      return;
    }
    var key = JSON.stringify(quiz);
    if (recsFor === key && !box.hidden) return;
    recsFor = key;
    var sub = box.querySelector('[data-memory-recs-sub]');
    if (sub) sub.textContent = engine.summary ? cap(engine.summary(quiz)) : '';
    var body = box.querySelector('[data-memory-recs-body]');
    box.hidden = false;
    engine.renderInto(body, quiz, { show: 8, railClass: 'profile-memory-recs-rail', note: false })
      .then(function (drew) {
        if (recsFor !== key) return;
        if (!drew && !body.textContent.trim()) box.hidden = true;
      });
  }

  function refreshLive() {
    var profile = profileNow();
    lists().forEach(function (list) {
      renderLive(list, profile);
      applyLimit(list);
      renderRecs(list, profile);
    });
    var overrides = all();
    Array.prototype.forEach.call(document.querySelectorAll('[data-memory-card]'), function (card) {
      paint(card, overrides);
    });
  }

  function onSeeAll(e) {
    var link = e.target.closest ? e.target.closest('[data-memory-see-all]') : null;
    if (!link) return false;
    if ((link.getAttribute('href') || '#') !== '#') return false;   /* a real page: let it go */
    e.preventDefault();
    var root = link.closest('.profile-section') || document;
    var list = root.querySelector('[data-memory-list]');
    if (!list) return true;
    list.classList.toggle('is-expanded');
    applyLimit(list);
    return true;
  }

  function run() {
    if (!lists().length && !document.querySelector('[data-memory-card]')) return;

    refreshLive();

    /* This file is loaded by the profile section, which the layout prints
       before vro-personalization.js; both are deferred, so this runs first
       and finds no store. Draw again the moment the store announces
       itself, and once more on load as a safety net, so a shopper's
       memories show without waiting for the next save. */
    document.addEventListener('vro:personalization:ready', refreshLive);
    window.addEventListener('load', refreshLive);

    document.addEventListener('click', function (e) { onSeeAll(e); });

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);

    /* The profile arrives from the server a moment after the page does
       (VroPersonalization pulls it on load), and it may carry edits made
       on another device. */
    document.addEventListener('vro:profile:change', function () {
      /* A new quiz answer (or one from another device) redraws the
         shopper's own cards — unless one is being edited right now, in
         which case only the wording is refreshed and the list is left
         where the finger is. */
      if (document.querySelector('[data-memory-card].is-editing')) {
        var fresh = all();
        Array.prototype.forEach.call(document.querySelectorAll('[data-memory-card]'), function (card) {
          if (card.classList.contains('is-editing')) return;   /* mid-edit, leave it alone */
          paint(card, fresh);
        });
        return;
      }
      refreshLive();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }
})();
